import React, { useEffect, useState } from 'react';
import styles from './Presentation.module.scss';
import { PresentationDialog } from '../../shared/VideoDialog';
import VideoPreviewDialog from '../../shared/VideoPreviewDialog';
import VideoCard from '../../shared/VideoCard';
import Loader from '../../shared/Loader';
import { useNotification } from '../../shared/NotificationSystem';
import { useTranslation } from 'react-i18next';
import { AddIcon } from '../../icons';
import ConfirmDialog from '../../shared/ConfirmDialog';
import { usePresentation } from '../../../redux/hooks/usePresentation';
import { useVideoProcessingStatus } from '../../../hooks/useVideoProcessingStatus';
import type { ITraining } from '../../../types';

const Presentation: React.FC = () => {
    const { t } = useTranslation();
    const { showSuccess, showError } = useNotification();
    const [newVideoIds, setNewVideoIds] = useState<Set<number>>(new Set());

    const {
        videos,
        selectedVideo,
        isLoading,
        deleteDialog,
        openDialog,
        fetchVideos,
        handleSubmit,
        handleDelete,
        confirmDelete,
        handleOpenDialog,
        handleVideoClick,
        handleCloseVideoPreview,
        handleCancelDelete,
        updateVideoFromStatus
    } = usePresentation();

    useEffect(() => {
        fetchVideos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = async (data: any) => {
        try {
            const result = await handleSubmit(data);

            // Проверяем, добавлялось ли новое видео
            const wasAdding = videos.length === 0;

            if (result && wasAdding && typeof result === 'object' && 'type' in result && result.type.endsWith('/fulfilled') && 'payload' in result && result.payload && typeof result.payload === 'object' && 'id' in result.payload) {
                const videoId = (result.payload as any).id;
                setNewVideoIds(prev => new Set(prev).add(videoId));

                // Убираем ID из списка новых через некоторое время
                setTimeout(() => {
                    setNewVideoIds(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(videoId);
                        return newSet;
                    });
                }, 30000); // 30 секунд на обработку
            }

            showSuccess(videos.length > 0
                ? t('content.presentation.success.updated')
                : t('content.presentation.success.saved'));
        } catch (error) {
            console.error('Error submitting presentation video:', error);
            showError(t('content.presentation.errors.uploadFailed'));
        }
    };

    const onDelete = async (id: number) => {
        try {
            const video = videos.find(v => v.id === id);
            if (!video) return;

            handleDelete(id);
        } catch (error) {
            console.error('Error preparing delete:', error);
            showError(t('content.presentation.errors.deleteFailed'));
        }
    };

    const onConfirmDelete = async () => {
        try {
            const success = await confirmDelete();
            if (success) {
                showSuccess(t('content.presentation.success.deleted'));
            }
        } catch (error) {
            console.error('Error deleting video:', error);
            showError(t('content.presentation.errors.deleteFailed'));
        }
    };

    const hasVideo = videos.length > 0;
    const video = hasVideo ? videos[0] : null;

    // Хук для отслеживания статуса обработки видео (если есть видео)
    const { status: processingStatus, message: processingMessage } = useVideoProcessingStatus({
        videoId: video?.id || 0,
        autoRefresh: !!video?.id,
        refreshInterval: 3000,
        onStatusChange: (status, data) => {
            // Обновляем видео в Redux при получении новых данных
            if (video && updateVideoFromStatus && data) {
                const updates: Partial<ITraining> = {};

                // Обновляем video_url если получили
                if (data.url && data.url !== (typeof video.video_url === 'string' ? video.video_url : '')) {
                    (updates as any).video_url = data.url;
                }

                // Обновляем preview_url если получили
                if (data.preview_url && data.preview_url !== (typeof video.preview_url === 'string' ? video.preview_url : '')) {
                    (updates as any).preview_url = data.preview_url;
                }

                // Обновляем title если получили
                if (data.title && data.title !== video.title) {
                    updates.title = data.title;
                }

                // Если есть обновления, применяем их
                if (Object.keys(updates).length > 0) {
                    updateVideoFromStatus(video.id, updates);

                    // Если получили video_url или preview_url, убираем видео из списка новых
                    if ((updates.video_url || updates.preview_url)) {
                        setNewVideoIds(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(video.id);
                            return newSet;
                        });
                    }
                }
            }
        }
    });

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h1 className={styles.title}>
                    {t('content.presentation.title')}
                </h1>
                <div className={styles.buttonGroup}>
                    <button
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        onClick={() => handleOpenDialog(true)}
                    >
                        <AddIcon /> {hasVideo ? t('content.presentation.changeButton') : t('content.presentation.addButton')}
                    </button>
                </div>
            </header>

            {isLoading ? (
                <div className={styles.emptyState}>
                    <Loader fullScreen={false} />
                </div>
            ) : hasVideo ? (
                <div className={styles.videoContainer}>
                    <VideoCard
                        key={video!.id}
                        id={video!.id}
                        title=""
                        thumbnailUrl={typeof video!.preview_url === 'string' ? video!.preview_url : ''}
                        videoUrl={typeof video!.video_url === 'string' ? video!.video_url : ''}
                        processingStatus={processingStatus}
                        processingMessage={processingMessage}
                        onEdit={() => handleOpenDialog(true)}
                        onDelete={() => onDelete(video!.id)}
                        onClick={() => handleVideoClick(video!)}
                        hideTitle
                        horizontal={true}
                        showProcessingIndicator={processingStatus !== 'completed'}
                        isNewVideo={newVideoIds.has(video!.id)}
                    />
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <p className={styles.emptyStateText}>{t('content.presentation.noVideo')}</p>
                </div>
            )}

            <PresentationDialog
                open={openDialog}
                onClose={() => handleOpenDialog(false)}
                onSubmit={onSubmit}
                dialogTitle={t('content.presentation.uploadDialog.title')}
                initialData={video ? {
                    videoType: video.video_type as 'youtube' | 'file',
                    youtubeUrl: video.youtube_url,
                    language: video.language as 'ru' | 'it',
                } : undefined}
            />

            <VideoPreviewDialog
                open={!!selectedVideo}
                onClose={handleCloseVideoPreview}
                title=""
                videoUrl={selectedVideo?.youtube_url || (typeof selectedVideo?.video_url === 'string' ? selectedVideo.video_url : '')}
                isYouTube={selectedVideo?.video_type === 'youtube'}
            />

            <ConfirmDialog
                open={deleteDialog.open}
                title={t('content.presentation.deleteDialog.title')}
                message={t('content.presentation.deleteDialog.message')}
                onConfirm={onConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </div>
    );
};

export default Presentation;